import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { usePortalParticipants } from "../../context/PortalParticipantsContext";
import { AppAvatar } from "../../components/AppAvatar";
import { messageAuthorAvatarSrc } from "../../lib/portalAvatars";

function formatAccessDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

function PortalAccessBanner({ portalAccess }) {
  if (!portalAccess || portalAccess.status === "active" || portalAccess.status === "no_user") return null;

  const contact = portalAccess.primary_contact_name || portalAccess.primary_contact_email || "this client";
  const invitedAt = formatAccessDate(portalAccess.magic_link_invited_at);
  const loginInfoAt = formatAccessDate(portalAccess.login_info_sent_at);

  if (portalAccess.status === "not_invited") {
    return (
      <div className="mb-6 neo-panel px-5 py-4" data-testid="portal-access-banner">
        <div className="label-eyebrow mb-2">Portal access</div>
        <p className="text-sm text-[var(--t-text)]">
          {contact} has not been invited yet. Send an Invite to workspace from Engagements.
        </p>
      </div>
    );
  }

  return (
    <div className="neo-panel mb-6 px-5 py-4 ring-1 ring-[var(--t-accent)]/25" data-testid="portal-access-banner">
      <div className="label-eyebrow mb-2 text-[var(--t-accent)]">Portal setup pending</div>
      <p className="text-sm text-[var(--t-text)]">
        {contact} was invited but has not finished portal setup yet.
      </p>
      <div className="mt-3 space-y-1 text-xs text-[var(--t-meta)]">
        {invitedAt ? <p>Magic link sent {invitedAt}</p> : null}
        {loginInfoAt ? <p>Login info sent {loginInfoAt}</p> : null}
        {portalAccess.temporary_password ? (
          <p data-testid="portal-access-temp-password">
            Temporary password: <span className="font-heading text-[var(--t-text)]">{portalAccess.temporary_password}</span>
          </p>
        ) : (
          <p>If the magic link did not work, use Resend invite from Engagements.</p>
        )}
      </div>
    </div>
  );
}

export default function ActivityPage() {
  const { client, user } = useAuth();
  const participants = usePortalParticipants();
  const [items, setItems] = useState([]);
  const [portalAccess, setPortalAccess] = useState(null);
  const [error, setError] = useState(null);
  const isAdmin = user?.role === "admin";
  const params = isAdmin ? `?client_id=${client?.id}` : "";

  useEffect(() => {
    if (!client) return;
    api.get(`/portal/activity${params}`)
      .then(({ data }) => {
        if (Array.isArray(data)) {
          setItems(data);
          setPortalAccess(null);
          return;
        }
        setItems(data?.items || []);
        setPortalAccess(data?.portal_access || null);
      })
      .catch((e) => setError(e.response?.status === 403 ? "Activity feed is available for Operational Ecosystem engagements only." : "Could not load activity."));
  }, [client, isAdmin, params]);

  if (!client) return <div className="p-12 t-meta">No client context.</div>;
  if (error && !isAdmin) return (
    <div
      className="t-fade-up min-w-0 max-w-full overflow-x-hidden p-4 text-left sm:p-5 md:p-7"
      data-testid="activity-page"
    >
      <div className="px-1 py-2.5 sm:px-2 md:px-0">
        <div className="neo-panel text-center py-16" data-testid="activity-not-available">
          <p className="t-meta">{error}</p>
        </div>
      </div>
    </div>
  );

  const groups = items.reduce((acc, it) => {
    const day = new Date(it.ts).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
    if (!acc[day]) acc[day] = [];
    acc[day].push(it);
    return acc;
  }, {});

  return (
    <div
      className="t-fade-up min-w-0 max-w-full overflow-x-hidden p-4 text-left sm:p-5 md:p-7"
      data-testid="activity-page"
    >
      <div className="px-1 py-2.5 sm:px-2 md:px-0">
        {isAdmin ? <PortalAccessBanner portalAccess={portalAccess} /> : null}
        {Object.keys(groups).length === 0 ? (
          <div className="neo-panel t-meta text-center py-16">No activity yet.</div>
        ) : (
          <div className="space-y-12">
            {Object.entries(groups).map(([day, dayItems]) => (
              <section key={day} data-testid={`activity-group-${day}`}>
                <div className="label-eyebrow min-w-0 mb-3">{day}</div>
                <div className="overflow-hidden neo-panel">
                  {dayItems.map((it) => (
                      <div key={it.id} className="flex items-start gap-4 border-b border-black/5 px-5 py-4 last:border-b-0 t-row-hover" data-testid={`activity-${it.id}`}>
                        <AppAvatar
                          name={it.actor_name}
                          src={messageAuthorAvatarSrc(
                            { author_role: it.actor_role, author_name: it.actor_name },
                            { participants, user, client },
                          )}
                          size={32}
                          className="shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm">
                            <span className="font-heading !font-light">{it.actor_name}</span>
                            <span className="text-[var(--t-meta)]"> {it.action} </span>
                            <span className="font-heading !font-light">{it.entity_title}</span>
                          </div>
                          <div className="t-meta text-xs mt-1">
                            {new Date(it.ts).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })} · {it.entity_type} · {it.actor_role}
                          </div>
                        </div>
                      </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
